import {
  Add as AddIcon,
  Cancel as CancelIcon,
  CheckCircle as CompleteIcon,
  HourglassEmpty as PendingIcon,
  Pending as ProcessingIcon,
  LocalShipping as ShippingIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import { format } from "date-fns-jalali";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchMyOrders } from "../../../application/actions/orderActions";

const OrderList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const orders = useSelector((state) => state.orders.myOrders.data);
  const loading = useSelector((state) => state.orders.myOrders.loading);

  // Local State
  const [statusFilter, setStatusFilter] = useState("all");
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 100,
  });

  useEffect(() => {
    dispatch(fetchMyOrders());
  }, [dispatch]);

  const handleViewDetails = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  const handleCreateOrder = () => {
    navigate("/orders/create");
  };

  const getOrderStatusIcon = (status) => {
    switch (status) {
      case "Pending":
        return <PendingIcon />;
      case "Processing":
        return <ProcessingIcon />;
      case "Shipped":
        return <ShippingIcon />;
      case "Delivered":
        return <CompleteIcon />;
      case "Cancelled":
        return <CancelIcon />;
      default:
        return <PendingIcon />;
    }
  };

  const getOrderStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "Processing":
        return "info";
      case "Shipped":
        return "primary";
      case "Delivered":
        return "success";
      case "Cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getOrderStatusLabel = (status) => {
    const labels = {
      Pending: "در انتظار",
      Processing: "در حال پردازش",
      Shipped: "ارسال شده",
      Delivered: "تحویل داده شده",
      Cancelled: "لغو شده",
    };
    return labels[status] || status;
  };

  const filteredOrders =
    statusFilter === "all"
      ? orders
      : orders?.filter((order) => order.status === statusFilter);

  const paginatedOrders = filteredOrders?.slice(
    (pagination.pageNumber - 1) * pagination.pageSize,
    pagination.pageNumber * pagination.pageSize
  );

  const totalPages = Math.ceil(
    (filteredOrders?.length || 0) / pagination.pageSize
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            سفارشات من
          </Typography>
          <Typography variant="body1" color="text.secondary">
            مشاهده و مدیریت سفارشات
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={handleCreateOrder}
        >
          ثبت سفارش جدید
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>فیلتر بر اساس وضعیت</InputLabel>
              <Select
                value={statusFilter}
                label="فیلتر بر اساس وضعیت"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">همه سفارشات</MenuItem>
                <MenuItem value="Pending">در انتظار</MenuItem>
                <MenuItem value="Processing">در حال پردازش</MenuItem>
                <MenuItem value="Shipped">ارسال شده</MenuItem>
                <MenuItem value="Delivered">تحویل داده شده</MenuItem>
                <MenuItem value="Cancelled">لغو شده</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography variant="body2" color="text.secondary">
              {filteredOrders?.length || 0} سفارش یافت شد
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Orders List */}
      {!loading && (
        <>
          {paginatedOrders && paginatedOrders.length > 0 ? (
            <Grid container spacing={3}>
              {paginatedOrders.map((order) => (
                <Grid item xs={12} key={order.id}>
                  <Card
                    sx={{
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 3,
                      },
                    }}
                  >
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        {/* Order Number & Date */}
                        <Grid item xs={12} md={3}>
                          <Typography variant="h6" gutterBottom>
                            سفارش #{order.orderNumber}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {order.createdAt &&
                              format(
                                new Date(order.createdAt),
                                "yyyy/MM/dd - HH:mm"
                              )}
                          </Typography>
                        </Grid>

                        {/* Status */}
                        <Grid item xs={12} md={2}>
                          <Chip
                            icon={getOrderStatusIcon(order.status)}
                            label={getOrderStatusLabel(order.status)}
                            color={getOrderStatusColor(order.status)}
                            sx={{ fontWeight: "bold" }}
                          />
                        </Grid>

                        {/* Items Count */}
                        <Grid item xs={12} md={2}>
                          <Typography variant="body2" color="text.secondary">
                            تعداد اقلام
                          </Typography>
                          <Typography variant="h6">
                            {order.itemsCount || order.items?.length || 0} عدد
                          </Typography>
                        </Grid>

                        {/* Total Amount */}
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            مبلغ کل
                          </Typography>
                          <Typography
                            variant="h6"
                            color="primary"
                            fontWeight="bold"
                          >
                            {order.totalAmount?.toLocaleString("fa-IR")} ریال
                          </Typography>
                        </Grid>

                        {/* Actions */}
                        <Grid item xs={12} md={2}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<ViewIcon />}
                            onClick={() => handleViewDetails(order.id)}
                          >
                            جزئیات
                          </Button>
                        </Grid>
                      </Grid>

                      {/* Delivery Address (if available) */}
                      {order.deliveryAddress && (
                        <Box
                          sx={{
                            mt: 2,
                            pt: 2,
                            borderTop: 1,
                            borderColor: "divider",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            آدرس تحویل: {order.deliveryAddress}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                سفارشی یافت نشد
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                هنوز سفارشی ثبت نکرده‌اید
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateOrder}
              >
                ثبت سفارش جدید
              </Button>
            </Paper>
          )}

          {/* Pagination */}
          {paginatedOrders && paginatedOrders.length > 0 && totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={totalPages}
                page={pagination.pageNumber}
                onChange={(e, value) =>
                  setPagination({ ...pagination, pageNumber: value })
                }
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default OrderList;
